import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { Newspaper } from 'lucide-react';
import { useLandingContent } from '../../hooks/useLandingContent';

const FALLBACK_PRESS_QUOTES = [
  {
    quoteEs: 'Joven rancaguino se integra a O\'Higgins gracias a app de captación de talentos',
    quoteEn: 'Young man from Rancagua, Chile, joins O\'Higgins thanks to talent acquisition app',
  },
  {
    quoteEs: 'BtalentStar: La app que acerca la captación de talento futbolístico al mercado internacional',
    quoteEn: 'BtalentStar: The app that brings soccer talent acquisition to the international market',
  },
  {
    quoteEs: 'Son más de 30 casos de éxito los que ha logrado esta plataforma, sin duda los más emblemáticos son los que además de integrarse a clubes profesionales, lograron ser parte de la Selección Chilena',
    quoteEn: 'There are more than 30 success stories made possible by this platform, without a doubt the more emblematic ones being those who, in addition to joining professional clubs, made the Chilean National team',
  },
  {
    quoteEs: 'Niños se integran al club de sus sueños gracias a nueva app',
    quoteEn: 'Kids join club of their dreams thanks to new app',
  },
];

export function PressQuotes() {
  const { i18n } = useTranslation();
  const isSpanish = i18n.language === 'es';
  const { data } = useLandingContent();

  const pressQuotes = data?.press_quotes?.length
    ? data.press_quotes.map((q) => ({ quoteEs: q.quote_es, quoteEn: q.quote_en }))
    : FALLBACK_PRESS_QUOTES;

  return (
    <section className="relative py-20 md:py-24 overflow-hidden bg-transparent">
      <div className="absolute inset-0 bg-brand-darkNavy/70 backdrop-blur-sm" />
      <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-brand-yellow/20 to-transparent" />

      <div className="relative z-10 container mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-12"
        >
          <motion.div
            className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-brand-yellow/10 border border-brand-yellow/30 mb-6"
            whileHover={{ scale: 1.05 }}
          >
            <Newspaper className="w-4 h-4 text-brand-yellow" />
            <span className="text-sm font-bold text-brand-yellow uppercase tracking-wider">
              {isSpanish ? 'Prensa' : 'Press'}
            </span>
          </motion.div>
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-black text-white">
            {isSpanish ? 'Lo que dice la prensa' : 'What the press says'}
          </h2>
        </motion.div>

        {/* Quotes grid */}
        <div className="grid md:grid-cols-2 gap-6 max-w-5xl mx-auto">
          {pressQuotes.map((item, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: index * 0.08 }}
              whileHover={{ y: -4 }}
              className="relative bg-brand-navy/60 backdrop-blur-xl border border-brand-lightBlue/20 rounded-2xl p-6 overflow-hidden group hover:border-brand-yellow/30 transition-colors duration-300"
            >
              {/* Corner accent */}
              <div className="absolute top-0 left-0 w-8 h-8 border-t-2 border-l-2 border-brand-yellow/30 rounded-tl-2xl" />
              <div className="absolute bottom-0 right-0 w-8 h-8 border-b-2 border-r-2 border-brand-yellow/30 rounded-br-2xl" />

              {/* Quote mark */}
              <div className="text-brand-yellow/20 text-6xl font-serif leading-none mb-2 select-none">"</div>

              <p className="text-dark-200 text-sm md:text-base leading-relaxed italic">
                {isSpanish ? item.quoteEs : item.quoteEn}
              </p>

              {/* Hover glow */}
              <div className="absolute inset-0 bg-brand-yellow/3 opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none" />
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}
